import { check401, getValidAccessToken } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

/**
 * Vertical-scoped facility-type catalog.
 *
 * The full GET /api/v1/facility-types endpoint returns every facility type
 * the app supports — too noisy for the non-healthcare add-facility form's
 * type dropdown. This module wraps the Rules server's
 * GET /api/v1/facility-types/by-vertical?vertical=<slug> endpoint, which
 * returns only types scoped to the requested vertical (e.g. "restaurant"
 * and "food_truck" for vertical=food_service).
 *
 * Falls back gracefully (returns an empty list) when the vertical is not
 * yet seeded — the form still renders so the user can proceed with a
 * manual type entry, and the backend's create flow stamps the vertical
 * regardless of whether the dropdown was used.
 */

export interface FacilityTypeByVerticalRow {
  id: number;
  type_slug: string;
  display_name: string;
  category?: string | null;
  vertical?: string | null;
}

export interface FacilityTypesByVerticalResponse {
  vertical: string;
  country_code: string;
  facility_types: FacilityTypeByVerticalRow[];
  count: number;
}

export async function getFacilityTypesByVertical(
  vertical: string,
  countryCode: string = "USA",
): Promise<FacilityTypesByVerticalResponse> {
  const baseURL = getApiOrigin();
  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const params = new URLSearchParams({
    vertical,
    country_code: countryCode || "USA",
  });

  const response = await fetch(
    `${baseURL}/api/v1/facility-types/by-vertical?${params.toString()}`,
    { method: "GET", headers },
  );

  check401(response);

  if (!response.ok) {
    // Fall back to an empty list so the form still renders. The user can
    // proceed with a manual type entry; the server stamps the vertical
    // regardless of whether the dropdown was used.
    return {
      vertical,
      country_code: countryCode || "USA",
      facility_types: [],
      count: 0,
    };
  }

  return (await response.json()) as FacilityTypesByVerticalResponse;
}
